import { PlayArrowOutlined } from "@mui/icons-material";
import { Box, Typography } from "@mui/material";
import Stack from "@mui/material/Stack";
import { useEffect } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useParams, useSearchParams } from "react-router-dom";

import { chooseDossierVehicleSourcingOption } from "../../../api/dossier/chooseDossierVehicleSourcingOption";
import APContactHelpButton from "../../../components/buttons/APContactHelpButton";
import APIcon from "../../../components/buttons/APIcon";
import BackButton from "../../../components/buttons/BackButton";
import HomeButton from "../../../components/buttons/HomeButton";
import InfoButton from "../../../components/buttons/InfoButton";
import FaqList from "../../../components/faq/FaqList";
import APFileUpload from "../../../components/form/APFileUpload";
import APForm from "../../../components/form/APForm";
import APRadioGroup, {
  RadioGroupOption,
} from "../../../components/form/APRadioGroup";
import PageContainer from "../../../components/PageContainer";
import RestrictedAppScope from "../../../components/RestrictedAppScope";
import { useFaqs } from "../../../hooks/useFaqs";
import { useVehicleSourcingAccess } from "../../../hooks/useVehicleSourcingAccess";
import { useAuth } from "../../../providers/AuthProvider";
import {
  ACTION_CONFIRMATION_EVENT_IDS,
  VehicleSourcingOption,
} from "../../../types/Dossier";

type Inputs = {
  vehicleSourcingOption: VehicleSourcingOption | null;
  vehiclePricingFile: File | null;
};

const VehicleSourcingMethod = () => {
  const { dossierId } = useParams();
  const [searchParams] = useSearchParams();
  const { getScopedPageUrl } = useAuth();
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<Inputs>({ mode: "onTouched" });

  const fromConfirmation = searchParams.get("fromConfirmation") === "true";
  const watchVehicleSourcingOption = watch("vehicleSourcingOption");

  if (!dossierId) {
    // TODO: improve client side error messaging
    throw new Error("dossier id param is misisng");
  }

  const {
    dossier,
    navigationCheckComplete,
    isLoading: isDossierLoading,
    isSuccess: isDossierSuccess,
  } = useVehicleSourcingAccess({
    dossierId,
  });

  const {
    faqProps,
    isLoading: isLoadingFaqs,
    isSuccess: isSuccessFaqs,
  } = useFaqs({
    pageId: "select-vehicle-sourcing",
    contextIds: {
      opportunityId: dossier?.data?.opportunity?.id,
    },
  });

  const { mutateAsync, isLoading: isLoadingMutate } =
    chooseDossierVehicleSourcingOption(dossierId ?? "", {
      mutationOptions: {
        successNavPath:
          watchVehicleSourcingOption === "self-sourced"
            ? getScopedPageUrl(
                "dossierActionConfirmation",
                dossierId,
                ACTION_CONFIRMATION_EVENT_IDS.VEHICLE_SOURCING_DRIVER_SOURCED_VEHICLE
              )
            : getScopedPageUrl(
                "dossierVehicleSourcingSelectVehicle",
                dossierId
              ),
        successReplaceHistory: watchVehicleSourcingOption === "self-sourced",
      },
    });

  const isLoading = isDossierLoading || isLoadingFaqs || isLoadingMutate;
  const isSuccess = isDossierSuccess && isSuccessFaqs;

  const renderAppLocation = faqProps.renderAppLocation;

  const vehicleSourcingOptions: RadioGroupOption[] = [
    {
      id: "request-sourcing",
      label: "Please get me vehicle pricing",
      belowField: renderAppLocation(
        "SelectVehicleSourcing-LimitVehiclePricing",
        <Box sx={{ display: "flex", alignItems: "start", columnGap: 1 }} ml={4}>
          <APIcon name="warning" color="warning" size="small" />
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Typography variant="labelLarge">
              You can only request pricing for 1 vehicle.
            </Typography>
            <Typography variant="labelLarge" fontWeight={600}>
              New vehicles only.
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      id: "self-sourced",
      label: "I have/will source my own vehicle",
      belowField: (
        <Box sx={{ display: "flex", flexDirection: "column", my: 1 }}>
          <APFileUpload
            name="vehiclePricingFile"
            label={"Upload Vehicle Contract/Quote/Tax Invoice"}
            control={control}
            errors={errors}
            validations={{ required: true }}
          />
        </Box>
      ),
    },
  ];

  const onSubmit: SubmitHandler<Inputs> = async (_data) => {
    if (_data.vehicleSourcingOption) {
      mutateAsync({
        vehicleSourcingOption: _data.vehicleSourcingOption,
        vehiclePricingFile: _data.vehiclePricingFile ?? null,
      });
    }
  };

  useEffect(() => {
    if (
      !!watchVehicleSourcingOption &&
      watchVehicleSourcingOption !== "self-sourced"
    ) {
      setValue("vehiclePricingFile", null);
    }
  }, [watchVehicleSourcingOption]);

  return (
    <PageContainer
      loading={isLoading || !navigationCheckComplete}
      title="Vehicle Sourcing"
      titleButton={
        isSuccess ? (
          <Box mr={1}>
            {renderAppLocation(
              "SelectVehicleSourcing-Page",
              <InfoButton />,
              true,
              true
            )}
          </Box>
        ) : undefined
      }
    >
      {isSuccess && navigationCheckComplete && (
        <Stack spacing={2}>
          <Box>
            <APForm
              onSubmit={handleSubmit(onSubmit)}
              submitText="Next"
              submitIcon={<PlayArrowOutlined />}
              isLoading={false}
              isError={Object.keys(errors).length > 0}
              leftButton={
                <Box
                  sx={{
                    display: "flex",
                    columnGap: 1,
                    justifyContent: "space-between",
                  }}
                >
                  <Box
                    sx={{ display: "flex", columnGap: 1, alignItems: "center" }}
                  >
                    {fromConfirmation ? (
                      <HomeButton
                        destination="dossierHome"
                        dossierId={dossierId}
                      />
                    ) : (
                      <BackButton />
                    )}
                  </Box>
                </Box>
              }
            >
              <APRadioGroup
                name="vehicleSourcingOption"
                label="Please select how your vehicle will be sourced."
                validations={{ required: true }}
                errors={errors}
                control={control}
                defaultValue={null}
                options={vehicleSourcingOptions}
                hideRequired
              />

              <Box>
                <FaqList
                  pageId="select-vehicle-sourcing"
                  appLocationIdentifier="SelectVehicleSourcing-FAQList"
                  faqContextIds={{
                    opportunityId: dossier.data?.opportunity?.id,
                  }}
                />
              </Box>
            </APForm>
            <Box>
              <RestrictedAppScope scope="external">
                <APContactHelpButton
                  regardingOpportunityId={
                    dossier.data?.opportunity?.id ?? undefined
                  }
                />
              </RestrictedAppScope>
            </Box>
          </Box>
        </Stack>
      )}
    </PageContainer>
  );
};

export default VehicleSourcingMethod;
